/* =====================================================================
   NMSC LABS: nmsc-orb.js
   The one lit object on the page: a dark glass sphere with a yellow rim, used as the glyph in the
   wordmark, inline in the statement headline and large in the footer.

   The HTML build rendered it with Three.js (a 130 KB gzip library, three WebGL contexts and meshes of
   20 to 65 thousand triangles with per-vertex noise). This is the same shading, written as a single
   fragment shader on a two-triangle quad (about 3 KB): the sphere is intersected analytically, the
   simplex noise colours the glass and bends the silhouette, and the lighting terms are the original
   ones (two diffuse lights, a fresnel rim, a specular highlight).

   API: window.NMSCOrb = { mount(canvas), mountAll(root), unmountAll(root) }
   Falls back to a CSS gradient (.nmsc-orb-fallback) without WebGL, and renders a still frame with
   reduced motion. Only visible canvases are drawn.
   ===================================================================== */
(function () {
  'use strict';

  var NOISE = [
    'vec3 mod289(vec3 x){ return x - floor(x * (1.0 / 289.0)) * 289.0; }',
    'vec4 mod289(vec4 x){ return x - floor(x * (1.0 / 289.0)) * 289.0; }',
    'vec4 permute(vec4 x){ return mod289(((x * 34.0) + 1.0) * x); }',
    'vec4 taylorInvSqrt(vec4 r){ return 1.79284291400159 - 0.85373472095314 * r; }',
    'float snoise(vec3 v){',
    '  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);',
    '  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);',
    '  vec3 i  = floor(v + dot(v, C.yyy));',
    '  vec3 x0 = v - i + dot(i, C.xxx);',
    '  vec3 g = step(x0.yzx, x0.xyz);',
    '  vec3 l = 1.0 - g;',
    '  vec3 i1 = min(g.xyz, l.zxy);',
    '  vec3 i2 = max(g.xyz, l.zxy);',
    '  vec3 x1 = x0 - i1 + C.xxx;',
    '  vec3 x2 = x0 - i2 + C.yyy;',
    '  vec3 x3 = x0 - D.yyy;',
    '  i = mod289(i);',
    '  vec4 p = permute(permute(permute(',
    '            i.z + vec4(0.0, i1.z, i2.z, 1.0))',
    '          + i.y + vec4(0.0, i1.y, i2.y, 1.0))',
    '          + i.x + vec4(0.0, i1.x, i2.x, 1.0));',
    '  float n_ = 0.142857142857;',
    '  vec3 ns = n_ * D.wyz - D.xzx;',
    '  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);',
    '  vec4 x_ = floor(j * ns.z);',
    '  vec4 y_ = floor(j - 7.0 * x_);',
    '  vec4 x = x_ * ns.x + ns.yyyy;',
    '  vec4 y = y_ * ns.x + ns.yyyy;',
    '  vec4 h = 1.0 - abs(x) - abs(y);',
    '  vec4 b0 = vec4(x.xy, y.xy);',
    '  vec4 b1 = vec4(x.zw, y.zw);',
    '  vec4 s0 = floor(b0) * 2.0 + 1.0;',
    '  vec4 s1 = floor(b1) * 2.0 + 1.0;',
    '  vec4 sh = -step(h, vec4(0.0));',
    '  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;',
    '  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;',
    '  vec3 p0 = vec3(a0.xy, h.x);',
    '  vec3 p1 = vec3(a0.zw, h.y);',
    '  vec3 p2 = vec3(a1.xy, h.z);',
    '  vec3 p3 = vec3(a1.zw, h.w);',
    '  vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));',
    '  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;',
    '  vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);',
    '  m = m * m;',
    '  return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));',
    '}'
  ].join('\n');

  var VERT = 'attribute vec2 aPos; varying vec2 vPos; void main(){ vPos = aPos; gl_Position = vec4(aPos, 0.0, 1.0); }';

  // Camera: 32 degree vertical fov, 3.6 units from the sphere (the HTML build's Three.js setup).
  var FRAG = [
    '#ifdef GL_FRAGMENT_PRECISION_HIGH',
    'precision highp float;',
    '#else',
    'precision mediump float;',
    '#endif',
    'varying vec2 vPos;',
    'uniform float uTime;',
    'uniform float uAmp;',
    'uniform float uFreq;',
    'uniform float uScale;',
    'uniform float uPx;',
    'uniform mat3 uRot;',
    'uniform vec3 uColorA;',
    'uniform vec3 uColorB;',
    'uniform vec3 uColorC;',
    NOISE,
    'void main(){',
    '  float tanH = 0.28674538575880794;',
    '  vec3 ro = vec3(0.0, 0.0, 3.6);',
    '  vec3 rd = normalize(vec3(vPos * tanH, -1.0));',
    '  float R = uScale;',
    '  float b = dot(ro, rd);',
    '  float tc = -b;',
    '  vec3 pc = ro + rd * tc;',
    '  float h = length(pc);',
    '  float disc = b * b - (dot(ro, ro) - R * R);',
    '  vec3 N;',
    '  if (disc > 0.0) { N = normalize(ro + rd * (-b - sqrt(disc))); } else { N = pc / max(h, 1e-4); }',
    '  vec3 No = N * uRot;',
    '  float t = uTime;',
    '  float n1 = snoise(No * uFreq + vec3(t * 0.35, t * 0.21, t * 0.27));',
    '  float n2 = snoise(No * uFreq * 2.4 - vec3(t * 0.3, 0.0, t * 0.18));',
    '  float d = n1 * uAmp + n2 * uAmp * 0.32;',
    '  float Rd = R * (1.0 + d);',
    '  float alpha = 1.0 - smoothstep(Rd - uPx * 1.2, Rd + uPx * 0.2, h);',
    '  if (alpha <= 0.0) { discard; }',
    '  vec3 V = normalize(ro - N * Rd);',
    '  float fres = pow(1.0 - max(dot(V, N), 0.0), 4.6);',
    '  vec3 L1 = normalize(vec3(-0.6, 0.85, 1.0));',
    '  vec3 L2 = normalize(vec3(-0.8, -0.3, 0.6));',
    '  float diff = max(dot(N, L1), 0.0) * 0.85 + max(dot(N, L2), 0.0) * 0.25;',
    '  float spec = pow(max(dot(reflect(-L1, N), V), 0.0), 48.0);',
    '  float k = smoothstep(-uAmp, uAmp, d);',
    '  vec3 base = mix(uColorA, uColorB, k);',
    '  vec3 col = base * (0.5 + 0.8 * diff) + uColorC * fres * 0.7 + vec3(1.0) * spec * 0.25;',
    '  gl_FragColor = vec4(col * alpha, alpha);',
    '}'
  ].join('\n');

  var PRESETS = {
    hero: { amp: 0.12, freq: 1.3, speed: 0.55, scale: 1.3 },
    inline: { amp: 0.13, freq: 1.5, speed: 0.7, scale: 1 },
    footer: { amp: 0.12, freq: 1.15, speed: 0.45, scale: 1 },
    def: { amp: 0.16, freq: 1.35, speed: 0.6, scale: 1 }
  };

  var instances = [];
  var pointer = { x: 0, y: 0, tx: 0, ty: 0 };
  var pointerBound = false;
  var looping = false;
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function hexToRgb(str, fallback) {
    var s = (str || '').trim();
    var m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s);
    if (m) {
      var h = m[1].length === 3 ? m[1].replace(/./g, '$&$&') : m[1];
      return [parseInt(h.slice(0, 2), 16) / 255, parseInt(h.slice(2, 4), 16) / 255, parseInt(h.slice(4, 6), 16) / 255];
    }
    var r = /rgba?\((\d+)[ ,]+(\d+)[ ,]+(\d+)/.exec(s);
    if (r) return [r[1] / 255, r[2] / 255, r[3] / 255];
    return fallback;
  }

  // The HTML build's Three.js turned each hex colour into linear light and wrote it to the canvas untouched,
  // which is what makes the glass read as near-black. Same maths here.
  function linear(rgb) {
    return rgb.map(function (c) { return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); });
  }

  // Euler XYZ (as Three.js composes it): the vector is rotated about Z, then Y, then X.
  function rotation(rx, ry, rz) {
    var cx = Math.cos(rx), sx = Math.sin(rx), cy = Math.cos(ry), sy = Math.sin(ry), cz = Math.cos(rz), sz = Math.sin(rz);
    // R = Rx * Ry * Rz, written row-major then handed to GL column-major
    var r00 = cy * cz, r01 = -cy * sz, r02 = sy;
    var r10 = cx * sz + sx * sy * cz, r11 = cx * cz - sx * sy * sz, r12 = -sx * cy;
    var r20 = sx * sz - cx * sy * cz, r21 = sx * cz + cx * sy * sz, r22 = cx * cy;
    return new Float32Array([r00, r10, r20, r01, r11, r21, r02, r12, r22]);
  }

  function compile(gl, type, src) {
    var sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  }

  function fallback(canvas) {
    canvas.classList.add('nmsc-orb-fallback');
    return null;
  }

  function bindPointer() {
    if (pointerBound) return;
    pointerBound = true;
    window.addEventListener('pointermove', function (e) {
      pointer.tx = (e.clientX / window.innerWidth) * 2 - 1;
      pointer.ty = (e.clientY / window.innerHeight) * 2 - 1;
    }, { passive: true });
  }

  function mount(canvas, options) {
    if (!canvas || canvas.__nmscOrb) return canvas && canvas.__nmscOrb;
    var kind = canvas.dataset.orb || 'def';
    var opts = {};
    var preset = PRESETS[kind] || PRESETS.def;
    for (var k in preset) opts[k] = preset[k];
    for (var o in (options || {})) opts[o] = options[o];

    var gl = null;
    try {
      gl = canvas.getContext('webgl', { alpha: true, antialias: false, powerPreference: 'low-power', premultipliedAlpha: true }) ||
        canvas.getContext('experimental-webgl');
    } catch (e) { gl = null; }
    if (!gl) return fallback(canvas);

    var vs = compile(gl, gl.VERTEX_SHADER, VERT);
    var fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return fallback(canvas);
    var prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return fallback(canvas);
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW); // one oversized triangle
    var aPos = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
    gl.clearColor(0, 0, 0, 0);

    var u = {};
    ['uTime', 'uAmp', 'uFreq', 'uScale', 'uPx', 'uRot', 'uColorA', 'uColorB', 'uColorC'].forEach(function (n) {
      u[n] = gl.getUniformLocation(prog, n);
    });
    var rim = hexToRgb(getComputedStyle(document.documentElement).getPropertyValue('--nmsc-trunk'), [0.949, 0.769, 0]);
    gl.uniform1f(u.uAmp, opts.amp);
    gl.uniform1f(u.uFreq, opts.freq);
    gl.uniform1f(u.uScale, opts.scale);
    var cA = linear([0.106, 0.114, 0.133]); // #1B1D22
    var cB = linear([0.369, 0.384, 0.420]); // #5E626B
    var cC = linear(rim);
    gl.uniform3f(u.uColorA, cA[0], cA[1], cA[2]);
    gl.uniform3f(u.uColorB, cB[0], cB[1], cB[2]);
    gl.uniform3f(u.uColorC, cC[0], cC[1], cC[2]);

    var inst = {
      canvas: canvas, gl: gl, u: u, opts: opts, time: Math.random() * 100, spin: 0,
      visible: false, w: 0, h: 0, last: 0, dirty: true, ro: null, io: null
    };

    function resize() {
      var cssW = canvas.clientWidth, cssH = canvas.clientHeight;
      if (!cssW || !cssH) return;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var w = Math.max(1, Math.round(cssW * dpr)), h = Math.max(1, Math.round(cssH * dpr));
      if (w === inst.w && h === inst.h) return;
      inst.w = w; inst.h = h;
      canvas.width = w; canvas.height = h;
      gl.viewport(0, 0, w, h);
      // one device pixel expressed in world units at the sphere (for the anti-aliased silhouette)
      gl.uniform1f(u.uPx, (2 * 0.28674538575880794 * 3.6) / h);
      inst.dirty = true;
    }
    inst.resize = resize;

    inst.draw = function (now) {
      var dt = inst.last ? Math.min((now - inst.last) / 1000, 0.05) : 0;
      inst.last = now;
      if (!reduced && !(window.NMSC && window.NMSC.paused)) {
        inst.time += dt * opts.speed;
        inst.spin += dt * 0.25;
      }
      pointer.x += (pointer.tx - pointer.x) * 0.06;
      pointer.y += (pointer.ty - pointer.y) * 0.06;
      var parallax = 0.25;
      gl.uniform1f(u.uTime, inst.time);
      gl.uniformMatrix3fv(u.uRot, false, rotation(pointer.y * parallax * 0.6, inst.spin, -pointer.x * parallax * 0.4));
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      inst.dirty = false;
    };

    resize();
    if (window.ResizeObserver) {
      inst.ro = new ResizeObserver(resize);
      inst.ro.observe(canvas);
    } else {
      window.addEventListener('resize', resize);
    }
    if (window.IntersectionObserver) {
      inst.io = new IntersectionObserver(function (entries) {
        inst.visible = entries[entries.length - 1].isIntersecting;
        if (inst.visible) { inst.last = 0; start(); }
      }, { rootMargin: '120px' });
      inst.io.observe(canvas);
    } else {
      inst.visible = true;
    }

    canvas.addEventListener('webglcontextlost', function (e) {
      e.preventDefault();
      unmount(canvas);
      fallback(canvas);
    });

    canvas.__nmscOrb = inst;
    instances.push(inst);
    bindPointer();
    start();
    return inst;
  }

  function unmount(canvas) {
    var inst = canvas && canvas.__nmscOrb;
    if (!inst) return;
    if (inst.ro) inst.ro.disconnect();
    if (inst.io) inst.io.disconnect();
    var i = instances.indexOf(inst);
    if (i > -1) instances.splice(i, 1);
    var lose = inst.gl.getExtension && inst.gl.getExtension('WEBGL_lose_context');
    if (lose) lose.loseContext();
    delete canvas.__nmscOrb;
  }

  // One shared loop. It only runs while some orb is on screen (or still owes its first frame).
  function start() {
    if (looping) return;
    looping = true;
    var tick = function (now) {
      var any = false;
      for (var i = 0; i < instances.length; i++) {
        var inst = instances[i];
        if (!inst.visible && !inst.dirty) continue;
        if (reduced && !inst.dirty) continue;
        any = true;
        inst.draw(now);
      }
      if (any && !document.hidden) requestAnimationFrame(tick);
      else looping = false;
    };
    requestAnimationFrame(tick);
  }
  document.addEventListener('visibilitychange', function () { if (!document.hidden) start(); });

  function mountAll(root) {
    var list = (root || document).querySelectorAll('canvas.nmsc-orb-canvas');
    for (var i = 0; i < list.length; i++) mount(list[i]);
  }

  function unmountAll(root) {
    var list = (root || document).querySelectorAll('canvas.nmsc-orb-canvas');
    for (var i = 0; i < list.length; i++) unmount(list[i]);
  }

  window.NMSCOrb = { mount: mount, unmount: unmount, mountAll: mountAll, unmountAll: unmountAll };
})();
